import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
    A4_WIDTH, A4_HEIGHT, LABEL_WIDTH, LABEL_HEIGHT,
    LABELS_PER_PAGE, labelPosition,
} from "../print-layout";
import "../print-sheet.scss";

export interface SheetLabel {
    id: string;
    dataUrl: string;
    album: string;
    copies: number;
}

interface Props {
    labels: SheetLabel[];
    setLabels: React.Dispatch<React.SetStateAction<SheetLabel[]>>;
}

export const PrintSheet: React.FC<Props> = ({ labels, setLabels }) => {
    const [guides, setGuides] = useState(true);
    const [printing, setPrinting] = useState(false);
    const [error, setError] = useState("");
    const expanded: SheetLabel[] = [];
    labels.forEach(label => {
        for (let i = 0; i < label.copies; i++) expanded.push(label);
    });
    const pages: SheetLabel[][] = [];
    for (let i = 0; i < expanded.length; i += LABELS_PER_PAGE) {
        pages.push(expanded.slice(i, i + LABELS_PER_PAGE));
    }

    const renderPage = (page: SheetLabel[], pageIndex: number) => (
        <div className="a4-page" key={pageIndex}>
            {page.map((label, index) => {
                const position = labelPosition(index);
                return (
                    <img
                        key={`${label.id}-${index}`}
                        src={label.dataUrl}
                        alt={label.album || "Untitled label"}
                        className={`sheet-label${guides ? " cut-guides" : ""}`}
                        style={{
                            left: `${position.left / A4_WIDTH * 100}%`,
                            top: `${position.top / A4_HEIGHT * 100}%`,
                            width: `${LABEL_WIDTH / A4_WIDTH * 100}%`,
                            height: `${LABEL_HEIGHT / A4_HEIGHT * 100}%`,
                        }}
                    />
                );
            })}
            <div className="scale-check">50 mm — check with a ruler</div>
            <span className="sheet-page-number">MDthings · {pageIndex + 1} / {pages.length}</span>
        </div>
    );

    const print = async () => {
        setPrinting(true);
        setError("");
        try {
            // Print only after all snapshot images in the print surface are decoded.
            const images = Array.from(document.querySelectorAll<HTMLImageElement>(".print-output img"));
            await Promise.all(images.map(image => image.decode()));
            window.print();
        } catch {
            setError("The print images could not be loaded. Please try again.");
        } finally {
            setPrinting(false);
        }
    };

    return (
        <section className="print-sheet" aria-labelledby="print-heading">
            <h2 id="print-heading">A4 print sheet</h2>
            <p>Add the current label above, then keep designing to mix different labels.
                Each addition is a snapshot, so later edits won’t change labels already on the sheet.</p>
            <p className="print-instructions">
                <strong>Print settings:</strong> A4 (210 × 297 mm), portrait, actual size / 100% scale.
                Turn off “fit to page” and browser headers and footers.
                Labels are <strong>38 × 54 mm</strong>, with 15 mm paper margins.
                Check the 50 mm ruler on a plain-paper test print and check your disc’s label recess before cutting adhesive paper.
            </p>
            {labels.length === 0 ? (
                <div className="sheet-empty">No labels yet. Use “Add to A4 sheet” to start.</div>
            ) : (
                <>
                    <ul className="sheet-queue">
                        {labels.map(label => (
                            <li key={label.id}>
                                <img src={label.dataUrl} alt="" />
                                <span className="sheet-album">{label.album || "Untitled label"}</span>
                                <label className="sheet-copies">
                                    Copies
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        step="1"
                                        value={label.copies}
                                        aria-label={`Copies of ${label.album || "untitled label"}`}
                                        onChange={event => {
                                            const copies = Math.max(1, Math.min(100, Math.floor(Number(event.currentTarget.value) || 1)));
                                            setLabels(previous => previous.map(item => item.id === label.id ? { ...item, copies } : item));
                                        }}
                                    />
                                </label>
                                <button type="button" className="btn-flat" onClick={() =>
                                    setLabels(previous => previous.filter(item => item.id !== label.id))
                                }>Remove<span className="sr-only"> {label.album}</span></button>
                            </li>
                        ))}
                    </ul>
                    <div className="sheet-toolbar">
                        <label>
                            <input type="checkbox" checked={guides} onChange={event => setGuides(event.currentTarget.checked)} />
                            <span>Cutting guides</span>
                        </label>
                        <span aria-live="polite">{expanded.length} label{expanded.length !== 1 ? "s" : ""} · {pages.length} A4 page{pages.length !== 1 ? "s" : ""}</span>
                        <button type="button" className="btn" disabled={printing} onClick={print}>
                            {printing ? "Preparing…" : "Print A4 / Save as PDF"}
                        </button>
                        <button type="button" className="btn-flat" onClick={() => setLabels([])}>Clear sheet</button>
                    </div>
                    {error && <p role="alert">{error}</p>}
                    <p className="sheet-preview-caption">Sheet preview · {LABELS_PER_PAGE} labels per page · preview is not actual size</p>
                    <div className="sheet-previews">{pages.map(renderPage)}</div>
                </>
            )}
            {createPortal(
                <div className="print-output">{pages.map(renderPage)}</div>,
                document.body
            )}
        </section>
    );
};
